import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MuiMaterials } from "../@index.js";
import * as global from "../data/global.js";
import { saveSelectedSite } from "../data/global.js";
import SensorTemperatureGraph from "./SensorTemperatureGraph";
import SensorTemperatureGraphFilter from "./SensorTemperatureGraphFilter";
import {
    createWindowChromeTheme,
    getWindowChromeThemeColor,
    ModalBody,
    ModalContentTile,
    SiteSelectPhase,
    SiteWindowHeaderLeading,
    SiteWindowToolNav,
    ChromeHeaderBar,
    Spinner,
    SITE_WINDOW_IDS,
    ensureSiteInfosLoaded,
} from "./windowChrome/index.js";

const getThemeColor = getWindowChromeThemeColor;

const emptyFilterOptions = () => ({
    binId: null,
    binName: null,
    cableId: null,
    cableName: null,
    sensorId: null,
    sensorName: null,
    dateFrom: null,
    dateTo: null,
    hourFrom: null,
    hourTo: null,
});

const SensorTemperatureGraphWindow = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const autoLoadHandledRef = useRef(false);

    const [forceUpdate, setForceUpdate] = useState(0);
    const [phase, setPhase] = useState("select");
    const [sites, setSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const carriedSiteGuid =
        (location.state?.autoLoad && location.state?.siteGuid) || location.state?.siteGuid || "";
    const [pickerSiteGuid, setPickerSiteGuid] = useState(() => carriedSiteGuid || "");
    const [loadedSiteGuid, setLoadedSiteGuid] = useState("");
    const [loading, setLoading] = useState(() => Boolean(carriedSiteGuid && location.state?.autoLoad));
    const [filterOptions, setFilterOptions] = useState(emptyFilterOptions);

    const { theme, textPrimary, textSecondary, paper, border, pageBg } = useMemo(
        () => createWindowChromeTheme(),
        [forceUpdate],
    );

    useEffect(() => {
        const onTheme = () => setForceUpdate((n) => n + 1);
        window.addEventListener("themeChanged", onTheme);
        return () => window.removeEventListener("themeChanged", onTheme);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const apply = () => {
            const all = [...(global.gw.siteInfos || [])].filter((s) => s.siteGuid && !s.isHms);
            all.sort((a, b) =>
                (a.siteName || "").toLowerCase().localeCompare((b.siteName || "").toLowerCase()),
            );
            if (cancelled) return;
            setSites(all);
            setSitesLoading(false);
        };
        if ((global.gw.siteInfos || []).length > 0) {
            apply();
            return undefined;
        }
        setSitesLoading(true);
        ensureSiteInfosLoaded().then(() => {
            if (!cancelled) apply();
        });
        return () => {
            cancelled = true;
        };
    }, [forceUpdate]);

    // Seed carried site before paint so tool-switching does not flash the picker.
    useLayoutEffect(() => {
        if (!location.state?.autoLoad || !location.state?.siteGuid) return;
        setPickerSiteGuid(location.state.siteGuid);
    }, [location.state?.autoLoad, location.state?.siteGuid]);

    const applySiteSelection = useCallback(async (siteGuid) => {
        if (!siteGuid) return;
        setLoading(true);
        try {
            const want = global.normalizeGuidForCompare
                ? global.normalizeGuidForCompare(siteGuid)
                : String(siteGuid || "").toLowerCase();
            const info = (global.gw.siteInfos || []).find((s) => {
                const g = s?.siteGuid;
                if (!g) return false;
                const have = global.normalizeGuidForCompare
                    ? global.normalizeGuidForCompare(g)
                    : String(g).toLowerCase();
                return have === want;
            });

            if (info) {
                if (global.gw.selectedSite?.refreshId) {
                    clearTimeout(global.gw.selectedSite.refreshId);
                }
                if (
                    !global.gw.selectedSite
                    || global.normalizeGuidForCompare?.(global.gw.selectedSite.siteGuid)
                        !== global.normalizeGuidForCompare?.(info.siteGuid)
                ) {
                    global.gw.selectedSite = new global.Site();
                }
                global.gw.selectedSite.siteIndex = info.siteIndex;
                global.gw.selectedSite.siteGuid = info.siteGuid;
                global.gw.selectedSite.siteName = info.siteName;
                global.gw.selectedSite.lastScan = info.lastScan;
                saveSelectedSite?.();
            } else {
                global.gw.selectedSite.siteGuid = siteGuid;
            }

            await global.initializeCustomerSite(navigate);
            setFilterOptions(emptyFilterOptions());
            setLoadedSiteGuid(info?.siteGuid || siteGuid);
            setPickerSiteGuid(info?.siteGuid || siteGuid);
            setPhase("view");
        } catch (e) {
            console.error("SensorTemperatureGraphWindow: failed to load site", e);
            setPhase("select");
            setLoadedSiteGuid("");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        const siteGuid = location.state?.siteGuid;
        if (!siteGuid || !location.state?.autoLoad) return;
        if (autoLoadHandledRef.current === siteGuid) return;
        autoLoadHandledRef.current = siteGuid;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    }, [location.state, applySiteSelection]);

    const handleContinue = useCallback((guid) => {
        const next = guid || pickerSiteGuid;
        if (!next) return;
        setPickerSiteGuid(next);
        applySiteSelection(next);
    }, [pickerSiteGuid, applySiteSelection]);

    const handleHeaderSiteChange = useCallback((siteGuid) => {
        if (!siteGuid) return;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    }, [applySiteSelection]);

    const handleClose = () => {
        navigate("/sites", { replace: true });
    };

    const graphTheme = useMemo(() => {
        const isLight = global.gw.theme === "light";
        return MuiMaterials.createTheme({
            components: {
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            color: isLight ? "#666666" : "#a8abb1",
                            fontSize: "14px",
                            "&.Mui-focused": {
                                color: isLight ? "#333333" : "#f4cc8e",
                            },
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            fontSize: "14px",
                            textTransform: "none",
                            background: isLight ? "#e0e0e0" : "#2d2d2d",
                            color: isLight ? "#333333" : "rgb(244, 204, 142)",
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            color: isLight ? "#333333" : "#f4cc8e",
                            backgroundColor: isLight ? "#ffffff" : "#2d2d2d",
                            fontSize: "14px",
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: `1px solid ${isLight ? "#cccccc" : "rgba(244,204,142,0.5)"}`,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                border: `1px solid ${isLight ? "#666666" : "#f4cc8e"}`,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: `1px solid ${isLight ? "#666666" : "#f4cc8e"}`,
                            },
                        },
                        input: {
                            fontSize: "14px",
                            padding: "8.5px 14px",
                        },
                    },
                },
            },
        });
    }, [forceUpdate]);

    const activeSiteGuid = loadedSiteGuid || pickerSiteGuid || "";

    return (
        <MuiMaterials.ThemeProvider theme={theme}>
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column",
                    background: pageBg,
                    color: textPrimary,
                }}
            >
                {loading ? (
                    <MuiMaterials.Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: getThemeColor("rgba(232,232,232,0.7)", "rgba(26,26,26,0.7)"),
                        }}
                    >
                        <Spinner size="modal" />
                    </MuiMaterials.Box>
                ) : null}

                <ChromeHeaderBar
                    paper={paper}
                    border={border}
                    textSecondary={textSecondary}
                    onClose={handleClose}
                    closeAriaLabel="Close Sensor History Graph"
                    leading={
                        phase === "view" ? (
                            <SiteWindowHeaderLeading
                                toolTitle="Sensor History Graph"
                                siteWindowId={SITE_WINDOW_IDS.history}
                                siteGuid={activeSiteGuid || null}
                                textSecondary={textSecondary}
                                border={border}
                                showSiteSelect
                                siteSelectValue={activeSiteGuid}
                                onSiteChange={handleHeaderSiteChange}
                                sites={sites}
                                filterSite={(s) => !s.isHms}
                                siteSelectDisabled={loading || sitesLoading}
                                includeTool={false}
                            />
                        ) : null
                    }
                    tool={
                        <SiteWindowToolNav
                            toolTitle="Sensor History Graph"
                            siteWindowId={SITE_WINDOW_IDS.history}
                            siteGuid={activeSiteGuid || null}
                            textSecondary={textSecondary}
                        />
                    }
                    actions={
                        phase === "view" && !loading ? (
                            <MuiMaterials.ThemeProvider theme={graphTheme} key={`history-filter-theme-${forceUpdate}`}>
                                <SensorTemperatureGraphFilter
                                    key={`history-filter-${activeSiteGuid}`}
                                    filterOptions={filterOptions}
                                    setFilterOptions={setFilterOptions}
                                />
                            </MuiMaterials.ThemeProvider>
                        ) : null
                    }
                />

                {phase === "select" ? (
                    <SiteSelectPhase
                        value={pickerSiteGuid}
                        onChange={setPickerSiteGuid}
                        onContinue={handleContinue}
                        onCancel={handleClose}
                        loading={sitesLoading || loading}
                        filterSite={(s) => !s.isHms}
                    />
                ) : null}

                {phase === "view" && !loading ? (
                    <ModalBody className="hide-scrollbar">
                        <ModalContentTile
                            className="hide-scrollbar graphWindow"
                            sx={{
                                overflowY: "auto",
                                overflowX: "hidden",
                                p: "12px 16px",
                                pt: 2.5,
                            }}
                        >
                            <MuiMaterials.ThemeProvider theme={graphTheme} key={`history-graph-theme-${forceUpdate}`}>
                                <SensorTemperatureGraph
                                    key={`history-graph-${activeSiteGuid}`}
                                    filterOptions={filterOptions}
                                />
                            </MuiMaterials.ThemeProvider>
                        </ModalContentTile>
                    </ModalBody>
                ) : null}
            </div>
        </MuiMaterials.ThemeProvider>
    );
};

export default SensorTemperatureGraphWindow;
